import { map } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Functions from '../../../callable/functions';
import usePromise from '../../util/usePromise';
import PosicaoAtivo from '../../../models/analises/posicaoAtivo';
import moment from 'moment';
import { useUsuario } from '../../../providers/usuarioProvider';
import { doc, getDoc, getFirestore } from 'firebase/firestore';

export default function usePosicaoAtivoPreenchida(codigoAtivo: string) {
  const obterPosicaoAtivoPromise = useCallback(
    () =>
      new Functions().obterPosicaoAtivo({ codigoAtivo }).then((res) => {
        return map(res.data, PosicaoAtivo.fromJson);
      }),
    [codigoAtivo],
  );
  const { data: posicaoAtivo = [], loading } = usePromise(
    obterPosicaoAtivoPromise,
  );
  const { visualizado } = useUsuario();
  const [periodoVisualizado, setPeriodoVisualizado] = useState<{
    inicio?: moment.Moment;
    fim?: moment.Moment;
  }>({});

  useEffect(() => {
    if (!visualizado) {
      setPeriodoVisualizado({});
      return;
    }

    const usuarioRef = doc(getFirestore(), `usuarios/${visualizado}`);
    getDoc(usuarioRef)
      .then((snapshot) => {
        if (!snapshot.exists()) {
          setPeriodoVisualizado({});
          return;
        }
        setPeriodoVisualizado({
          inicio: snapshot.get('dataInicio')
            ? moment(snapshot.get('dataInicio').toDate())
            : undefined,
          fim: snapshot.get('atualizadoAte')
            ? moment(snapshot.get('atualizadoAte').toDate())
            : undefined,
        });
      })
      .catch(() => setPeriodoVisualizado({}));
  }, [visualizado]);

  const posicaoAtivoPreenchida = useMemo(() => {
    if (posicaoAtivo.length === 0) return [];

    const inicioPeriodo = periodoVisualizado.inicio;
    const fimPeriodo = periodoVisualizado.fim;

    // Sem período definido: apenas filtra mudanças (comportamento original)
    if (!inicioPeriodo || !fimPeriodo) {
      return posicaoAtivo.filter((item, index) => {
        if (index === 0) return true;
        const anterior = posicaoAtivo[index - 1];
        return (
          item.quantidade !== anterior.quantidade ||
          item.precoMedio !== anterior.precoMedio
        );
      });
    }

    const inicio = inicioPeriodo.clone().startOf('day');
    const fim = fimPeriodo.clone().startOf('day');
    const { codigoAtivo: cod, classeAtivo: classe } = posicaoAtivo[0];

    // 1. Monta lista base com os pontos do histórico
    const pontos: PosicaoAtivo[] = [...posicaoAtivo];

    // 2. Insere ponto zero no início do período se não existir dado na data exata
    const temDadoNoInicio = pontos.some((p) => p.data.isSame(inicio, 'day'));
    if (!temDadoNoInicio) {
      pontos.unshift(
        new PosicaoAtivo({
          codigoAtivo: cod,
          classeAtivo: classe,
          data: inicio,
          quantidade: 0,
          precoMedio: 0,
          valorAplicado: 0,
        }),
      );
    }

    // 3. Insere ponto zero no fim do período se não houver dado lá
    const ultimaData = pontos[pontos.length - 1].data;
    if (ultimaData.isBefore(fim)) {
      pontos.push(
        new PosicaoAtivo({
          codigoAtivo: cod,
          classeAtivo: classe,
          data: fim,
          quantidade: 0,
          precoMedio: 0,
          valorAplicado: 0,
        }),
      );
    }

    // 4. Preenche gaps: para cada par consecutivo, se há intervalo > 1 dia,
    //    insere zero no dia seguinte ao ponto anterior
    const preenchidos: PosicaoAtivo[] = [pontos[0]];
    for (let i = 1; i < pontos.length; i++) {
      const anterior = pontos[i - 1];
      const atual = pontos[i];
      if (atual.data.diff(anterior.data, 'days') > 1) {
        preenchidos.push(
          new PosicaoAtivo({
            codigoAtivo: cod,
            classeAtivo: classe,
            data: anterior.data.clone().add(1, 'day'),
            quantidade: 0,
            precoMedio: 0,
            valorAplicado: 0,
          }),
        );
      }
      preenchidos.push(atual);
    }

    // 5. Remove pontos consecutivos com mesmo valor (otimização para o gráfico)
    const resultado = preenchidos.filter((item, index) => {
      if (index === 0) return true;
      const anterior = preenchidos[index - 1];
      return (
        item.quantidade !== anterior.quantidade ||
        item.precoMedio !== anterior.precoMedio
      );
    });

    // 6. Garante que o último ponto (data = fim) está presente no resultado
    const ultimoResultado = resultado[resultado.length - 1];
    const ultimoPreenchido = preenchidos[preenchidos.length - 1];
    if (ultimoResultado !== ultimoPreenchido) {
      resultado.push(ultimoPreenchido);
    }

    return resultado;
  }, [posicaoAtivo, periodoVisualizado]);

  return { posicaoAtivoPreenchida, loading };
}
